import {useState} from 'react';
import {
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  notification,
} from 'antd';
import {
  CalendarOutlined,
  PercentageOutlined,
  TagOutlined,
} from '@ant-design/icons';
import moment, {Moment} from 'moment';
import {getCurrentUser} from 'aws-amplify/auth';
import {
  Package,
  Curriculum,
  Level,
  Classschool,
  School,
  PikaDoPartners,
} from '../../../graphql/API';
import {SchoolsDropDown} from '../../../components/SchoolsDropDown';
import type {
  NewCreateDiscountCodeInput,
  NewDiscountCodeType,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/discountCode/graphql/discountCode.interface';

const {Text} = Typography;

interface CreateDiscountCodeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  packages: Package[];
  curriculums: Curriculum[];
  levels: Level[];
  onNeedLevels: () => void;
  createDiscountCode: (input: NewCreateDiscountCodeInput) => Promise<any>;
}

export const CreateDiscountCodeModal = ({
  open,
  onClose,
  onSuccess,
  packages,
  curriculums,
  levels,
  onNeedLevels,
  createDiscountCode,
}: CreateDiscountCodeModalProps) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSchoolDiscount, setIsSchoolDiscount] = useState(false);
  const [schoolId, setSchoolId] = useState<string>();
  const [classId, setClassId] = useState<string>();
  const [partnerFromSchool, setPartnerFromSchool] = useState<PikaDoPartners>();

  const handleClose = () => {
    form.resetFields();
    setIsSchoolDiscount(false);
    setSchoolId(undefined);
    setClassId(undefined);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      // Validate school discount fields
      if (isSchoolDiscount && (!schoolId || !classId || !values.levelId)) {
        notification.error({
          message: 'Missing School Fields',
          description: 'School discount requires school, class, and level.',
        });
        setIsSubmitting(false);
        return;
      }

      // Validate date range
      if (moment(values.validFrom).isAfter(values.expiryDate)) {
        notification.error({
          message: 'Invalid Date Range',
          description: 'Valid From must be before Expiry Date.',
        });
        setIsSubmitting(false);
        return;
      }

      let createdBy = 'unknown';
      try {
        const user = await getCurrentUser();
        createdBy = user.signInDetails?.loginId || user.userId || 'unknown';
      } catch {}

      const input: NewCreateDiscountCodeInput = {
        code: values.code,
        value: values.value,
        type: values.type as NewDiscountCodeType,
        expiryDate: values.expiryDate?.toISOString(),
        packageIds: [values.packageId],
        curriculumId: values.curriculumId,
        partnerId: values.partnerId !== 'none' ? values.partnerId : undefined,
        limit: values.limit,
        createdBy,
        ...(isSchoolDiscount
          ? {
              schoolId,
              classId,
              levelId: values.levelId,
            }
          : {}),
      };

      await createDiscountCode(input);

      notification.success({
        message: 'Discount Code Created!',
        description: `Code "${values.code}" has been created successfully.`,
        duration: 4,
      });

      handleClose();
      onSuccess();
    } catch (err: any) {
      if (err?.errorFields) return;
      notification.error({
        message: 'Failed to Create Code',
        description: err?.message || 'An unexpected error occurred.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultCurriculum = curriculums.find(c => c.id === 'PIKADO');

  return (
    <Modal
      title={
        <>
          <TagOutlined /> &nbsp; Create Discount Code
        </>
      }
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Create Code"
      confirmLoading={isSubmitting}
      width={640}
      style={{top: 20}}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: 'PERCENTAGE',
          value: 0,
          limit: 100,
          curriculumId: defaultCurriculum?.id ?? 'PIKADO',
          validFrom: moment(),
          expiryDate: moment().add(30, 'days'),
        }}>
        {/* Code + Type */}
        <Row gutter={16}>
          <Col span={14}>
            <Form.Item
              name="code"
              label="Discount Code"
              rules={[{required: true, message: 'Code is required'}]}>
              <Input
                placeholder="e.g. SUMMER25"
                style={{textTransform: 'uppercase'}}
                onChange={e =>
                  form.setFieldValue('code', e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name="type"
              label="Discount Type"
              rules={[{required: true}]}>
              <Select>
                <Select.Option value="PERCENTAGE">
                  <PercentageOutlined /> Percentage
                </Select.Option>
                <Select.Option value="FIXED">💵 Fixed Amount</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Value */}
        <Form.Item
          name="value"
          label="Discount Value"
          rules={[
            {required: true, message: 'Value is required'},
            {type: 'number', min: 0.01, message: 'Must be greater than 0'},
          ]}>
          <InputNumber
            style={{width: '100%'}}
            min={0}
            max={form.getFieldValue('type') === 'PERCENTAGE' ? 100 : undefined}
            placeholder="e.g. 25"
            addonAfter={
              <Form.Item name="type" noStyle>
                <Select
                  style={{width: 80}}
                  onChange={v => form.setFieldValue('type', v)}>
                  <Select.Option value="PERCENTAGE">%</Select.Option>
                  <Select.Option value="FIXED">$</Select.Option>
                </Select>
              </Form.Item>
            }
          />
        </Form.Item>

        {/* Package */}
        <Form.Item
          name="packageId"
          label="Package"
          rules={[{required: true, message: 'Please select a package'}]}>
          <Select
            showSearch
            placeholder="Select a package"
            optionFilterProp="children">
            {packages.map(pkg => {
              const id = (pkg as any)._id || pkg.id;
              const name =
                typeof pkg.name_i18n === 'string'
                  ? JSON.parse(pkg.name_i18n)['en']
                  : (pkg.name_i18n as any)?.en;
              return (
                <Select.Option key={id} value={id}>
                  {name}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>

        {/* Usage Limit */}
        <Form.Item
          name="limit"
          label="Usage Limit"
          rules={[
            {
              required: true,
              message: 'Please specify how many times this code can be used',
            },
          ]}>
          <InputNumber min={1} style={{width: '100%'}} placeholder="e.g. 100" />
        </Form.Item>

        {/* Date Range */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="validFrom"
              label={
                <>
                  <CalendarOutlined /> &nbsp; Valid From
                </>
              }
              rules={[{required: true, message: 'Required'}]}>
              <DatePicker
                style={{width: '100%'}}
                showTime={{defaultValue: moment('00:00:00', 'HH:mm:ss')}}
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expiryDate"
              label={
                <>
                  <CalendarOutlined /> &nbsp; Expiry Date
                </>
              }
              rules={[{required: true, message: 'Required'}]}>
              <DatePicker
                style={{width: '100%'}}
                showTime={{defaultValue: moment('23:59:59', 'HH:mm:ss')}}
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Curriculum + Partner */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="curriculumId"
              label="Curriculum"
              rules={[{required: true}]}>
              <Select showSearch optionFilterProp="children">
                {curriculums.map(c => (
                  <Select.Option key={c.id} value={c.id!}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="partnerId" label="Partner (optional)">
              <Select placeholder="None" allowClear>
                {Object.values(PikaDoPartners).map(p => (
                  <Select.Option key={p} value={p}>
                    {p.toLowerCase()}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* School Discount Toggle */}
        <Form.Item>
          <Checkbox
            checked={isSchoolDiscount}
            onChange={e => {
              setIsSchoolDiscount(e.target.checked);
              if (e.target.checked) onNeedLevels();
            }}>
            This is a school discount code
          </Checkbox>
        </Form.Item>

        {isSchoolDiscount && (
          <div
            style={{border: '1px solid #f0f0f0', borderRadius: 8, padding: 16}}>
            <SchoolsDropDown
              onSelectClass={(cls: Classschool) => setClassId(cls?.id)}
              onSelectSchool={(school: School) => setSchoolId(school?.id)}
              onSelectPartner={(partner: PikaDoPartners) => {
                setPartnerFromSchool(partner);
                form.setFieldValue('partnerId', partner);
              }}
            />
            <Form.Item
              name="levelId"
              label="Level"
              rules={[
                {
                  required: isSchoolDiscount,
                  message: 'Level is required for school discounts',
                },
              ]}
              style={{marginTop: 16, marginBottom: 0}}>
              <Select
                showSearch
                placeholder="Select a level"
                optionFilterProp="children">
                {levels.map(lvl => (
                  <Select.Option key={lvl.id} value={lvl.id!}>
                    {lvl.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  );
};
